<template>
  <div class="as-page">
    <AsPageHeader
      breadcrumb="Personal > Legajo"
      kicker="LEGAJO RRHH"
      :title="modulo.titulo"
      :subtitle="modulo.descripcion"
    >
      <template #actions>
        <input
          ref="fileImport"
          type="file"
          accept=".xlsx,.xls"
          class="d-none"
          @change="onArchivoImport"
        >
        <button
          v-if="puedeCrear"
          type="button"
          class="as-btn as-btn--primary"
          :disabled="loading"
          @click="abrirNuevo()"
        >
          <v-icon left color="white" size="18">mdi-plus</v-icon>
          Nuevo registro
        </button>
        <button
          v-if="esAdmin"
          type="button"
          class="as-btn as-btn--ghost"
          :disabled="loading || importando"
          @click="abrirSelectorExcel"
        >
          <v-icon left color="#1d4ed8" size="18">mdi-file-excel-outline</v-icon>
          {{ importando ? 'Importando…' : 'Importar Excel' }}
        </button>
        <button
          v-if="esAdmin"
          type="button"
          class="as-btn"
          :disabled="loading || importando"
          @click="descargarPlantilla"
        >
          <v-icon left size="18">mdi-download-outline</v-icon>
          Plantilla
        </button>
      </template>
    </AsPageHeader>

    <div class="as-stats">
      <div class="as-stat">
        <div class="as-stat__icon as-stat__icon--blue"><v-icon color="#1d4ed8">mdi-badge-account-horizontal-outline</v-icon></div>
        <div>
          <div class="as-stat__label">Registros</div>
          <div class="as-stat__value">{{ items.length }}</div>
        </div>
      </div>
      <div class="as-stat">
        <div class="as-stat__icon as-stat__icon--green"><v-icon color="#15803d">mdi-check-circle-outline</v-icon></div>
        <div>
          <div class="as-stat__label">Activos</div>
          <div class="as-stat__value">{{ activosCount }}</div>
        </div>
      </div>
      <div class="as-stat">
        <div class="as-stat__icon as-stat__icon--slate"><v-icon color="#475569">mdi-filter-variant</v-icon></div>
        <div>
          <div class="as-stat__label">Resultados</div>
          <div class="as-stat__value">{{ filtrados.length }}</div>
        </div>
      </div>
    </div>

    <v-alert v-if="errorClinica" type="info" dense outlined class="as-alert">
      {{ errorClinica }} Puede registrar personal de cualquier IPRESS indicando la clínica en el formulario.
    </v-alert>

    <section class="as-card as-card--flush">
      <div class="as-card__head">
        <div>
          <h2>Directorio de personal</h2>
          <p>Consulte, cree o edite fichas según su perfil.</p>
        </div>
      </div>
      <div class="as-filters">
        <v-autocomplete
          v-model="searchId"
          :items="opcionesBusqueda"
          :search-input.sync="searchText"
          :filter="filtrarOpcion"
          item-text="label"
          item-value="id"
          label="Buscar personal"
          placeholder="DNI, código, apellidos o cargo…"
          outlined
          dense
          clearable
          hide-details
          background-color="white"
          prepend-inner-icon="mdi-magnify"
          no-data-text="Sin coincidencias"
          class="as-search-select"
          @change="onBuscarSelect"
          @click:clear="limpiarBusqueda"
        >
          <template v-slot:item="{ item }">
            <div class="as-option">
              <strong>{{ item.apellidosNombres || '—' }}</strong>
              <span>
                DNI {{ item.dniCe || '—' }}
                <template v-if="item.codigoEmpleado"> · Cód. {{ item.codigoEmpleado }}</template>
                <template v-if="item.cargo"> · {{ item.cargo }}</template>
              </span>
            </div>
          </template>
          <template v-slot:selection="{ item }">
            <span class="as-selection-text">
              {{ item.apellidosNombres || '—' }}
              <small>({{ item.dniCe || item.codigoEmpleado || '—' }})</small>
            </span>
          </template>
        </v-autocomplete>
        <v-select
          v-model="filtroEstado"
          :items="estadoOptions"
          label="Estado"
          outlined dense hide-details clearable
          background-color="white"
          style="max-width: 180px"
        />
        <button type="button" class="as-btn as-btn--primary as-btn--buscador" @click="abrirBuscador">
          <v-icon left color="white" size="18">mdi-account-search-outline</v-icon>
          Buscador
        </button>
      </div>

      <div v-if="!$vuetify.breakpoint.xsOnly" class="as-table-wrap">
        <v-data-table
          :headers="headers"
          :items="filtrados"
          :loading="loading"
          item-key="id"
          :items-per-page="15"
          no-data-text="No hay registros de personal"
        >
          <template v-slot:item.estado="{ item }">
            <span class="as-chip" :class="item.estado ? 'as-chip--ok' : 'as-chip--off'">
              {{ item.estado ? 'Activo' : 'Inactivo' }}
            </span>
          </template>
          <template v-slot:item.acciones="{ item }">
            <div class="cp-actions">
              <v-btn small outlined color="info" class="cp-action-btn" @click="abrirConsulta(item)">
                <v-icon left small>mdi-eye-outline</v-icon>
                Consultar
              </v-btn>
              <v-btn
                v-if="puedeEditar"
                small outlined color="primary"
                class="cp-action-btn"
                @click="abrirEditar(item)"
              >
                <v-icon left small>mdi-pencil-outline</v-icon>
                Editar
              </v-btn>
              <v-btn
                v-if="puedeEditar"
                small outlined color="error"
                class="cp-action-btn"
                @click="eliminar(item)"
              >
                <v-icon left small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>

      <div v-else class="cp-cards">
        <article v-for="item in filtrados" :key="item.id" class="cp-card-item">
          <div class="cp-card-item__body">
            <div class="cp-card-row">
              <span class="cp-card-row__label">Personal</span>
              <span class="cp-card-row__value">{{ item.apellidosNombres || '—' }}</span>
            </div>
            <div class="cp-card-row">
              <span class="cp-card-row__label">DNI</span>
              <span class="cp-card-row__value">{{ item.dniCe || '—' }}</span>
            </div>
            <div class="cp-card-row">
              <span class="cp-card-row__label">Cargo</span>
              <span class="cp-card-row__value">{{ item.cargo || '—' }}</span>
            </div>
          </div>
          <div class="cp-card-item__actions">
            <v-btn small block outlined color="info" @click="abrirConsulta(item)">Consultar</v-btn>
            <v-btn v-if="puedeEditar" small block outlined color="primary" @click="abrirEditar(item)">Editar</v-btn>
          </div>
        </article>
      </div>
    </section>

    <!-- Formulario alta/edición -->
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="720"
      persistent
      scrollable
      content-class="lg-dialog"
    >
      <v-card class="lg-card" elevation="0">
        <div class="lg-header">
          <div>
            <h2 class="lg-title">{{ editando ? 'Editar ficha RRHH' : 'Nueva ficha RRHH' }}</h2>
            <p class="lg-subtitle">
              Complete los datos del personal. Paso {{ stepActual }} de {{ totalSteps }}
              <template v-if="seccionActual"> — {{ seccionActual.title }}</template>.
            </p>
          </div>
          <button type="button" class="lg-close" aria-label="Cerrar" @click="cerrarDialogo">
            <v-icon size="22" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <v-card-text class="lg-body">
          <div class="lg-progress">
            <div
              v-for="(sec, idx) in seccionesVisibles"
              :key="'p-' + sec.id"
              class="lg-progress__dot"
              :class="{
                'is-active': stepActual === idx + 1,
                'is-done': stepActual > idx + 1,
              }"
              :title="sec.title"
              @click="irAStep(idx + 1)"
            />
          </div>

          <div class="lg-foto">
            <div class="lg-foto__preview">
              <img v-if="fotoPreview" :src="fotoPreview" alt="Foto del personal">
              <v-icon v-else size="40" color="#94a3b8">mdi-account-circle-outline</v-icon>
            </div>
            <div class="lg-foto__actions">
              <label class="lg-label">Foto del personal</label>
              <p class="lg-foto__hint">JPG o PNG, se guarda con la ficha.</p>
              <div class="lg-foto__btns">
                <v-btn small outlined color="#0b5cad" @click="$refs.inputFoto.click()">
                  <v-icon left small>mdi-camera-outline</v-icon>
                  {{ fotoPreview ? 'Cambiar foto' : 'Subir foto' }}
                </v-btn>
                <v-btn
                  v-if="fotoPreview"
                  small
                  text
                  color="error"
                  @click="quitarFoto"
                >
                  Quitar
                </v-btn>
              </div>
              <input
                ref="inputFoto"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                class="d-none"
                @change="onSeleccionarFoto"
              >
            </div>
          </div>

          <div class="lg-grid lg-grid--meta">
            <div class="lg-field">
              <label class="lg-label">Clínica / IPRESS</label>
              <v-text-field
                v-model="form.clinica"
                placeholder="Nombre de la clínica"
                outlined
                dense
                hide-details
                class="lg-input"
                background-color="#fff"
              />
            </div>
            <div class="lg-field lg-field--switch">
              <label class="lg-label">Estado</label>
              <v-switch v-model="form.estado" label="Activo" inset dense hide-details class="mt-0" />
            </div>
          </div>

          <div
            v-for="(sec, idx) in seccionesVisibles"
            v-show="stepActual === idx + 1"
            :key="'sec-' + sec.id"
            class="lg-step-panel"
          >
            <h3 class="lg-step-title">{{ sec.title }}</h3>
            <div class="lg-grid">
              <div
                v-for="field in sec.fields"
                :key="field.key"
                class="lg-field"
                :class="field.cols === 12 ? 'lg-field--full' : ''"
              >
                <label class="lg-label">
                  {{ field.label }}
                  <span v-if="field.required" class="lg-req">*</span>
                </label>
                <v-select
                  v-if="field.type === 'select'"
                  :value="valorCampo(field)"
                  :items="field.items"
                  :placeholder="field.placeholder || 'Seleccionar'"
                  outlined
                  dense
                  hide-details="auto"
                  clearable
                  class="lg-input"
                  background-color="#fff"
                  @input="v => setValor(field, v)"
                />
                <v-text-field
                  v-else-if="field.type === 'date'"
                  :value="valorCampo(field)"
                  type="date"
                  outlined
                  dense
                  hide-details="auto"
                  class="lg-input"
                  background-color="#fff"
                  @input="v => setValor(field, v)"
                />
                <v-text-field
                  v-else-if="field.type === 'number'"
                  :value="valorCampo(field)"
                  type="number"
                  min="0"
                  :placeholder="field.placeholder || ''"
                  outlined
                  dense
                  hide-details="auto"
                  class="lg-input"
                  background-color="#fff"
                  @input="v => setValor(field, v)"
                />
                <v-text-field
                  v-else
                  :value="valorCampo(field)"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.type === 'readonly'"
                  outlined
                  dense
                  hide-details="auto"
                  class="lg-input"
                  :class="{ 'lg-input--ro': field.type === 'readonly' }"
                  background-color="#fff"
                  @input="v => setValor(field, v)"
                />
              </div>
            </div>
          </div>
        </v-card-text>

        <div class="lg-footer">
          <v-btn text class="lg-btn-text" @click="cerrarDialogo">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            v-if="step > 1"
            outlined
            color="#334155"
            class="lg-btn"
            @click="stepAnterior"
          >
            Anterior
          </v-btn>
          <v-btn
            v-if="!esUltimoStep"
            color="#0b5cad"
            dark
            elevation="0"
            class="lg-btn lg-btn--primary"
            @click="stepSiguiente"
          >
            Siguiente
          </v-btn>
          <v-btn
            v-else
            color="#0b5cad"
            dark
            elevation="0"
            class="lg-btn lg-btn--primary"
            :loading="saving"
            @click="guardar"
          >
            Guardar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Consulta solo lectura -->
    <v-dialog v-model="dialogConsulta" max-width="800" scrollable>
      <v-card v-if="consultaItem">
        <v-card-text class="pa-4">
          <legajo-detalle-lectura :item="consultaItem" :perfil-slug="perfilActivo" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogConsulta = false">Cerrar</v-btn>
          <v-btn
            v-if="puedeEditar"
            color="primary"
            text
            @click="abrirEditar(consultaItem); dialogConsulta = false"
          >
            Editar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Buscador flotante -->
    <v-dialog
      v-model="dialogBuscador"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="860"
      scrollable
      content-class="lg-dialog"
    >
      <v-card class="lg-card" elevation="0">
        <div class="lg-header">
          <div>
            <h2 class="lg-title">Buscador de personal</h2>
            <p class="lg-subtitle">Filtre por DNI, código, apellidos o cargo y seleccione para ver la ficha completa.</p>
          </div>
          <button type="button" class="lg-close" aria-label="Cerrar" @click="cerrarBuscador">
            <v-icon size="22" color="#64748b">mdi-close</v-icon>
          </button>
        </div>
        <v-card-text class="lg-body">
          <v-autocomplete
            v-model="buscadorId"
            :items="opcionesBusqueda"
            :search-input.sync="buscadorText"
            :filter="filtrarOpcion"
            item-text="label"
            item-value="id"
            label="Personal"
            placeholder="Escriba DNI, código, apellidos o cargo…"
            outlined
            dense
            clearable
            hide-details
            background-color="white"
            prepend-inner-icon="mdi-magnify"
            no-data-text="Sin coincidencias"
            class="mb-4"
            @change="onBuscadorSelect"
            @click:clear="limpiarBuscadorSelect"
          >
            <template v-slot:item="{ item }">
              <div class="as-option">
                <strong>{{ item.apellidosNombres || '—' }}</strong>
                <span>
                  DNI {{ item.dniCe || '—' }}
                  <template v-if="item.codigoEmpleado"> · Cód. {{ item.codigoEmpleado }}</template>
                  <template v-if="item.cargo"> · {{ item.cargo }}</template>
                </span>
              </div>
            </template>
            <template v-slot:selection="{ item }">
              <span class="as-selection-text">
                {{ item.apellidosNombres || '—' }}
                <small>({{ item.dniCe || item.codigoEmpleado || '—' }})</small>
              </span>
            </template>
          </v-autocomplete>

          <div v-if="!buscadorItem" class="as-empty as-empty--dialog">
            <v-icon size="48" color="#94a3b8">mdi-account-search-outline</v-icon>
            <p>Seleccione un colaborador para ver toda su información.</p>
          </div>
          <legajo-detalle-lectura
            v-else
            :item="buscadorItem"
            :perfil-slug="perfilActivo"
          />
        </v-card-text>
        <div class="lg-footer">
          <v-btn text class="lg-btn-text" @click="cerrarBuscador">Cerrar</v-btn>
          <v-spacer />
          <v-btn
            v-if="buscadorItem && puedeEditar"
            color="#0b5cad"
            dark
            elevation="0"
            class="lg-btn lg-btn--primary"
            @click="abrirEditarDesdeBuscador"
          >
            Editar ficha
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import {
  cpList,
  cpCreate,
  cpUpdate,
  cpRemove,
  LEGAJO_ENDPOINT,
  resolveClinicaUsuario,
  asegurarPerfilLegajo,
} from '../legajoApi'
import {
  MODULO_RRHH,
  blankLegajoRrhh,
  normalizarLegajoRrhh,
  payloadLegajoRrhh,
  valorCampoLegajo,
  setValorCampoLegajo,
  sincronizarCalculados,
  seccionesParaPerfil,
  resolverPerfilLegajo,
  configPerfilLegajo,
  puedeEditarLegajo,
  puedeCrearLegajo,
  puedeConsultarRegistro,
  puedeVerLegajoAjeno,
  esAdministradorLegajo,
  headersPlantillaLegajo,
  mapearCabecerasImport,
  filaExcelALegajo,
} from '../rrhhLegajo'
import LegajoDetalleLectura from '../LegajoDetalleLectura.vue'
import AsPageHeader from '../components/AsPageHeader.vue'

export default {
  name: 'RrhhLegajoPersonal',
  components: { LegajoDetalleLectura, AsPageHeader },
  data() {
    return {
      modulo: MODULO_RRHH,
      perfilActivo: resolverPerfilLegajo(),
      loading: false,
      saving: false,
      importando: false,
      dialog: false,
      dialogConsulta: false,
      dialogBuscador: false,
      editando: false,
      step: 1,
      searchId: null,
      searchText: '',
      buscadorId: null,
      buscadorText: '',
      buscadorItem: null,
      filtroEstado: null,
      items: [],
      form: blankLegajoRrhh(),
      consultaItem: null,
      clinicaNombre: '',
      errorClinica: '',
      estadoOptions: [
        { text: 'Todos', value: null },
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false },
      ],
      headers: [
        { text: 'N°', value: 'numero' },
        { text: 'Cód. empleado', value: 'codigoEmpleado' },
        { text: 'Apellidos y nombres', value: 'apellidosNombres' },
        { text: 'DNI / C.E.', value: 'dniCe' },
        { text: 'Cargo', value: 'cargo' },
        { text: 'Área', value: 'area' },
        { text: 'Clínica', value: 'clinica' },
        { text: 'Estado', value: 'estado' },
        { text: 'Acciones', value: 'acciones', sortable: false },
      ],
    }
  },
  computed: {
    perfilLabel() {
      return (configPerfilLegajo(this.perfilActivo) || {}).label || this.perfilActivo
    },
    puedeEditar() {
      return puedeEditarLegajo(this.perfilActivo)
    },
    puedeCrear() {
      return puedeCrearLegajo(this.perfilActivo)
    },
    esAdmin() {
      return esAdministradorLegajo(this.perfilActivo)
    },
    seccionesVisibles() {
      return seccionesParaPerfil(this.perfilActivo)
    },
    totalSteps() {
      return Math.max(this.seccionesVisibles.length, 1)
    },
    stepActual() {
      return Math.min(Math.max(Number(this.step) || 1, 1), this.totalSteps)
    },
    seccionActual() {
      return this.seccionesVisibles[this.stepActual - 1] || null
    },
    esUltimoStep() {
      return this.stepActual >= this.totalSteps
    },
    puedeVerAjeno() {
      return puedeVerLegajoAjeno(this.perfilActivo)
    },
    activosCount() {
      return this.items.filter(i => i.estado).length
    },
    opcionesBusqueda() {
      let list = this.items || []
      if (!this.puedeVerAjeno) {
        list = list.filter(i => puedeConsultarRegistro(i, this.perfilActivo))
      }
      return list.map((item) => ({
        ...item,
        label: [
          item.apellidosNombres || '',
          item.dniCe ? `DNI ${item.dniCe}` : '',
          item.codigoEmpleado ? `Cód. ${item.codigoEmpleado}` : '',
          item.cargo || '',
        ].filter(Boolean).join(' · '),
      }))
    },
    filtrados() {
      let list = this.items
      if (!this.puedeVerAjeno) {
        list = list.filter(i => puedeConsultarRegistro(i, this.perfilActivo))
      }
      if (this.filtroEstado !== null && this.filtroEstado !== undefined) {
        list = list.filter(i => i.estado === this.filtroEstado)
      }
      if (this.searchId != null && this.searchId !== '') {
        return list.filter(i => String(i.id) === String(this.searchId))
      }
      const q = (this.searchText || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(i =>
        ['apellidosNombres', 'dniCe', 'codigoEmpleado', 'cargo', 'area', 'clinica', 'numero']
          .some(k => String(i[k] || '').toLowerCase().includes(q)),
      )
    },
    fotoPreview() {
      return (this.form && this.form.datos && this.form.datos.foto) || ''
    },
  },
  async created() {
    this.perfilActivo = await asegurarPerfilLegajo()
    await this.resolverClinica()
    await this.cargar()
  },
  methods: {
    filtrarOpcion(item, queryText) {
      const q = String(queryText || '').trim().toLowerCase()
      if (!q) return true
      const qNorm = q.replace(/\s+/g, ' ')
      const qDni = q.replace(/\D/g, '')
      const qCodigo = q.replace(/\s+/g, '')
      const dni = String(item.dniCe || '').replace(/\D/g, '')
      const nom = String(item.apellidosNombres || '').toLowerCase()
      const codigo = String(item.codigoEmpleado || '').toLowerCase().replace(/\s+/g, '')
      const cargo = String(item.cargo || '').toLowerCase()
      const area = String(item.area || '').toLowerCase()
      if (qDni.length >= 3 && dni.includes(qDni)) return true
      if (codigo && codigo.includes(qCodigo)) return true
      if (nom.includes(qNorm)) return true
      if (cargo.includes(qNorm) || area.includes(qNorm)) return true
      const palabras = qNorm.split(' ').filter(Boolean)
      if (palabras.length > 1 && palabras.every(p => nom.includes(p))) return true
      return false
    },
    onBuscarSelect(id) {
      if (id == null || id === '') {
        this.searchId = null
        this.searchText = ''
      }
    },
    limpiarBusqueda() {
      this.searchId = null
      this.searchText = ''
    },
    abrirBuscador() {
      this.buscadorId = null
      this.buscadorText = ''
      this.buscadorItem = null
      this.dialogBuscador = true
    },
    cerrarBuscador() {
      this.dialogBuscador = false
      this.buscadorId = null
      this.buscadorText = ''
      this.buscadorItem = null
    },
    onBuscadorSelect(id) {
      this.buscadorItem = null
      if (id == null || id === '') return
      const elegido = (this.items || []).find(i => String(i.id) === String(id))
      if (!elegido) return
      if (!puedeConsultarRegistro(elegido, this.perfilActivo)) {
        Swal.fire({
          icon: 'info',
          title: 'Acceso restringido',
          text: 'Solo puede consultar su propio legajo.',
        })
        this.buscadorId = null
        return
      }
      this.buscadorItem = normalizarLegajoRrhh(elegido)
      sincronizarCalculados(this.buscadorItem)
    },
    limpiarBuscadorSelect() {
      this.buscadorId = null
      this.buscadorText = ''
      this.buscadorItem = null
    },
    abrirEditarDesdeBuscador() {
      if (!this.buscadorItem) return
      const item = this.buscadorItem
      this.cerrarBuscador()
      this.abrirEditar(item)
    },
    onSeleccionarFoto(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (this.$refs.inputFoto) this.$refs.inputFoto.value = ''
      if (!file) return
      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
        Swal.fire({ icon: 'info', title: 'Formato no válido', text: 'Use JPG, PNG o WEBP.' })
        return
      }
      if (file.size > 4 * 1024 * 1024) {
        Swal.fire({ icon: 'info', title: 'Archivo muy grande', text: 'Máximo 4 MB.' })
        return
      }
      this.comprimirFoto(file).then((dataUrl) => {
        if (!this.form.datos) this.form.datos = {}
        this.form.datos.foto = dataUrl
        this.$forceUpdate()
      }).catch(() => {
        Swal.fire({ icon: 'error', title: 'No se pudo leer la imagen' })
      })
    },
    comprimirFoto(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = reject
        reader.onload = () => {
          const img = new Image()
          img.onerror = reject
          img.onload = () => {
            const max = 480
            let { width, height } = img
            if (width > max || height > max) {
              const ratio = Math.min(max / width, max / height)
              width = Math.round(width * ratio)
              height = Math.round(height * ratio)
            }
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            resolve(canvas.toDataURL('image/jpeg', 0.82))
          }
          img.src = reader.result
        }
        reader.readAsDataURL(file)
      })
    },
    quitarFoto() {
      if (!this.form.datos) this.form.datos = {}
      this.form.datos.foto = null
      this.$forceUpdate()
    },
    async resolverClinica() {
      try {
        const res = await resolveClinicaUsuario()
        if (res.ok) {
          this.clinicaNombre = res.clinica.nombre || res.clinica.descripCas || ''
        } else {
          this.errorClinica = res.error
        }
      } catch (e) {
        this.errorClinica = 'No se pudo determinar la clínica del usuario.'
      }
    },
    async cargar() {
      this.loading = true
      try {
        const params = {}
        if (this.clinicaNombre) params.clinica = this.clinicaNombre
        this.items = await cpList(LEGAJO_ENDPOINT, params)
      } catch (e) {
        console.error(e)
        this.items = []
      } finally {
        this.loading = false
      }
    },
    valorCampo(field) {
      return valorCampoLegajo(this.form, field)
    },
    setValor(field, val) {
      setValorCampoLegajo(this.form, field, val)
      if (field.key === 'cargo' && !this.form.cargo) {
        this.form.cargo = val
      }
      this.$forceUpdate()
    },
    displayValor(item, field) {
      const f = normalizarLegajoRrhh(item)
      sincronizarCalculados(f)
      return valorCampoLegajo(f, field)
    },
    abrirNuevo() {
      if (!this.puedeCrear) return
      this.editando = false
      this.form = blankLegajoRrhh(this.clinicaNombre)
      this.step = 1
      this.dialog = true
    },
    abrirEditar(item) {
      if (!this.puedeEditar) return
      this.editando = true
      this.form = normalizarLegajoRrhh(item)
      sincronizarCalculados(this.form)
      this.step = 1
      this.dialog = true
    },
    abrirConsulta(item) {
      if (!puedeConsultarRegistro(item, this.perfilActivo)) {
        Swal.fire({
          icon: 'info',
          title: 'Acceso restringido',
          text: 'Solo puede consultar su propio legajo.',
        })
        return
      }
      this.consultaItem = normalizarLegajoRrhh(item)
      sincronizarCalculados(this.consultaItem)
      this.dialogConsulta = true
    },
    cerrarDialogo() {
      this.dialog = false
      this.step = 1
      this.form = blankLegajoRrhh(this.clinicaNombre)
    },
    irAStep(n) {
      const dest = Number(n) || 1
      if (dest < 1 || dest > this.totalSteps) return
      // Solo permitir ir a pasos ya visitados o el siguiente inmediato
      if (dest <= this.stepActual || dest === this.stepActual + 1) {
        if (dest > this.stepActual && !this.validarStepActual()) return
        this.step = dest
      }
    },
    stepAnterior() {
      if (this.step > 1) this.step -= 1
    },
    stepSiguiente() {
      if (!this.validarStepActual()) return
      if (this.step < this.totalSteps) this.step += 1
    },
    validarStepActual() {
      const sec = this.seccionActual
      if (!sec) return true
      const faltantes = (sec.fields || [])
        .filter(f => f.required)
        .filter(f => !String(this.valorCampo(f) || '').trim())
      if (!faltantes.length) return true
      Swal.fire({
        icon: 'info',
        title: 'Complete los campos obligatorios',
        text: faltantes.map(f => f.label).join(', '),
      })
      return false
    },
    async guardar() {
      if (!this.esUltimoStep) {
        Swal.fire({
          icon: 'info',
          title: 'Complete todos los pasos',
          text: 'Solo puede guardar en el último paso del formulario.',
        })
        return
      }
      if (!this.validarStepActual()) return
      // Validar obligatorios de todas las secciones visibles
      const faltantes = []
      this.seccionesVisibles.forEach((sec) => {
        (sec.fields || []).forEach((f) => {
          if (f.required && !String(this.valorCampo(f) || '').trim()) {
            faltantes.push(f.label)
          }
        })
      })
      if (faltantes.length) {
        Swal.fire({
          icon: 'info',
          title: 'Faltan datos obligatorios',
          text: faltantes.join(', '),
        })
        return
      }
      const veIdentidad = this.seccionesVisibles.some(s =>
        s.fields.some(f => f.key === 'apellidosNombres' || f.key === 'dniCe'),
      )
      if (veIdentidad) {
        if (!String(this.form.apellidosNombres || '').trim()) {
          Swal.fire({ icon: 'info', title: 'Ingrese apellidos y nombres' })
          return
        }
        if (!String(this.form.dniCe || '').trim()) {
          Swal.fire({ icon: 'info', title: 'Ingrese DNI o C.E.' })
          return
        }
      }
      this.saving = true
      try {
        sincronizarCalculados(this.form)
        const payload = payloadLegajoRrhh(this.form)
        if (!payload.clinica) payload.clinica = this.clinicaNombre

        if (this.editando && this.form.id) {
          await cpUpdate(LEGAJO_ENDPOINT, this.form.id, payload)
        } else {
          await cpCreate(LEGAJO_ENDPOINT, payload)
        }
        Swal.fire({ icon: 'success', title: 'Guardado', timer: 1800, showConfirmButton: false })
        this.cerrarDialogo()
        await this.cargar()
      } catch (e) {
        console.error(e)
        Swal.fire({ icon: 'error', title: 'Error al guardar' })
      } finally {
        this.saving = false
      }
    },
    async eliminar(item) {
      const r = await Swal.fire({
        icon: 'warning',
        title: '¿Eliminar registro?',
        text: item.apellidosNombres || '',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
      })
      if (!r.isConfirmed) return
      try {
        await cpRemove(LEGAJO_ENDPOINT, item.id)
        await this.cargar()
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'No se pudo eliminar' })
      }
    },
    abrirSelectorExcel() {
      if (!this.esAdmin) return
      if (this.$refs.fileImport) this.$refs.fileImport.value = ''
      this.$refs.fileImport.click()
    },
    descargarPlantilla() {
      const XLSX = require('xlsx-js-style')
      const headers = headersPlantillaLegajo()
      const ws = XLSX.utils.aoa_to_sheet([headers])
      ws['!cols'] = headers.map((h) => ({ wch: Math.max(14, String(h).length + 2) }))
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Legajo')
      XLSX.writeFile(wb, 'plantilla-rrhh-legajo.xlsx')
    },
    async onArchivoImport(evt) {
      const file = evt.target && evt.target.files && evt.target.files[0]
      if (!file || !this.esAdmin) return
      this.importando = true
      try {
        const buffer = await file.arrayBuffer()
        const XLSX = require('xlsx-js-style')
        const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
        const sheetName = wb.SheetNames[0]
        const sheet = wb.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true })
        if (!rows.length) {
          Swal.fire({ icon: 'info', title: 'El Excel está vacío' })
          return
        }
        const headerRow = rows[0]
        const colMap = mapearCabecerasImport(headerRow)
        if (colMap.apellidosNombres == null && colMap.dniCe == null) {
          Swal.fire({
            icon: 'warning',
            title: 'Cabeceras no reconocidas',
            text: 'Descargue la plantilla e use las mismas columnas.',
          })
          return
        }

        await this.cargar()
        const porDni = {}
        ;(this.items || []).forEach((it) => {
          const d = String(it.dniCe || '').replace(/\D/g, '')
          if (d) porDni[d] = it
        })

        let creados = 0
        let actualizados = 0
        let omitidos = 0
        const errores = []

        for (let i = 1; i < rows.length; i += 1) {
          const row = rows[i]
          if (!row || !row.some(c => c !== '' && c != null)) continue
          const form = filaExcelALegajo(row, colMap, this.clinicaNombre)
          const dni = String(form.dniCe || '').replace(/\D/g, '')
          const nombre = String(form.apellidosNombres || '').trim()
          if (!dni && !nombre) {
            omitidos += 1
            continue
          }
          if (!nombre) {
            errores.push(`Fila ${i + 1}: falta apellidos y nombres`)
            omitidos += 1
            continue
          }
          if (!dni) {
            errores.push(`Fila ${i + 1}: falta DNI`)
            omitidos += 1
            continue
          }
          try {
            const payload = payloadLegajoRrhh(form)
            if (!payload.clinica) payload.clinica = this.clinicaNombre
            const existente = porDni[dni]
            if (existente && existente.id) {
              const merged = normalizarLegajoRrhh(existente)
              Object.keys(payload).forEach((k) => {
                if (k === 'datos') return
                if (payload[k] !== '' && payload[k] != null) merged[k] = payload[k]
              })
              Object.keys(payload.datos || {}).forEach((k) => {
                const v = payload.datos[k]
                if (v !== null && v !== undefined && v !== '') {
                  merged.datos[k] = v
                }
              })
              await cpUpdate(LEGAJO_ENDPOINT, existente.id, payloadLegajoRrhh(merged))
              actualizados += 1
            } else {
              const creado = await cpCreate(LEGAJO_ENDPOINT, payload)
              if (creado && creado.id) porDni[dni] = creado
              creados += 1
            }
          } catch (e) {
            console.error(e)
            errores.push(`Fila ${i + 1}: error al guardar`)
            omitidos += 1
          }
        }

        await this.cargar()
        const detalleErrores = errores.length
          ? `<br><br><small>${errores.slice(0, 8).join('<br>')}${errores.length > 8 ? '<br>…' : ''}</small>`
          : ''
        Swal.fire({
          icon: errores.length ? 'warning' : 'success',
          title: 'Importación finalizada',
          html: `Creados: <strong>${creados}</strong><br>`
            + `Actualizados: <strong>${actualizados}</strong><br>`
            + `Omitidos: <strong>${omitidos}</strong>${detalleErrores}`,
        })
      } catch (e) {
        console.error(e)
        Swal.fire({ icon: 'error', title: 'No se pudo leer el Excel' })
      } finally {
        this.importando = false
        if (this.$refs.fileImport) this.$refs.fileImport.value = ''
      }
    },
  },
}
</script>

<style scoped>
@import '../rrhh-as.css';

.cp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
}

.cp-cards {
  padding: 0.5rem 1.2rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.cp-card-item {
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #fff;
  padding: 0.9rem;
}

.cp-card-item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.cp-card-item__id {
  font-size: 0.75rem;
  color: var(--muted);
  font-weight: 600;
}

.cp-card-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
}

.cp-card-row {
  display: grid;
  grid-template-columns: minmax(90px, 38%) 1fr;
  gap: 0.5rem;
}

.cp-card-row__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--muted);
  font-weight: 600;
}

.cp-card-row__value {
  font-size: 0.92rem;
  color: var(--text);
  font-weight: 500;
}

.cp-card-item__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.cp-btn-import {
  background: rgba(255, 255, 255, 0.92) !important;
  color: #15803d !important;
  border-color: rgba(255, 255, 255, 0.55) !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  min-height: 42px;
}
.cp-btn-plantilla {
  color: #fff !important;
  font-weight: 600 !important;
  text-transform: none !important;
  min-height: 42px;
}

.lg-card {
  border-radius: 14px !important;
  overflow: hidden;
  background: #fff !important;
}
.lg-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.35rem 1.5rem 0.35rem;
}
.lg-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.lg-subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.4;
}
.lg-close {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 8px;
  line-height: 0;
}
.lg-close:hover {
  background: #f1f5f9;
}
.lg-body {
  padding: 0.75rem 1.5rem 0.5rem !important;
}
.lg-progress {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 1.15rem;
}
.lg-progress__dot {
  flex: 1;
  height: 4px;
  border-radius: 99px;
  background: #e2e8f0;
  cursor: pointer;
  transition: background 0.15s ease;
}
.lg-progress__dot.is-active {
  background: #0b5cad;
}
.lg-progress__dot.is-done {
  background: #93c5fd;
}
.lg-step-title {
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}
.lg-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1.1rem;
}
.lg-grid--meta {
  margin-bottom: 1.15rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eef2f7;
}
.lg-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}
.lg-field--full {
  grid-column: 1 / -1;
}
.lg-field--switch {
  justify-content: flex-end;
}
.lg-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1e293b;
}
.lg-req {
  color: #dc2626;
  margin-left: 0.1rem;
}
.lg-input >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 40px !important;
}
.lg-input >>> fieldset {
  border-color: #dbe3ec !important;
  border-radius: 10px !important;
}
.lg-input >>> legend {
  display: none !important;
}
.lg-input >>> .v-text-field__slot,
.lg-input >>> .v-select__slot {
  padding-top: 0 !important;
}
.lg-input >>> .v-input__slot:hover fieldset {
  border-color: #94a3b8 !important;
}
.lg-input.v-input--is-focused >>> fieldset {
  border-color: #0b5cad !important;
  border-width: 1.5px !important;
}
.lg-input--ro >>> .v-input__slot {
  background: #f8fafc !important;
}
.lg-input--ro >>> input {
  color: #64748b !important;
}
.lg-footer {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1.5rem 1.25rem;
  border-top: 1px solid #eef2f7;
  background: #fff;
}
.lg-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  border-radius: 10px !important;
  min-height: 40px !important;
  padding: 0 1.1rem !important;
}
.lg-btn--primary {
  min-width: 110px;
}
.lg-btn-text {
  text-transform: none !important;
  color: #64748b !important;
  font-weight: 600 !important;
}

@media (max-width: 600px) {
  .lg-grid {
    grid-template-columns: 1fr;
  }
}

.cp-consulta-title {
  font-weight: 700;
}
.cp-consulta-body {
  padding-top: 0.5rem;
}
.cp-consulta-sec {
  margin-bottom: 1.25rem;
}
.cp-consulta-sec__title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #0f172a;
  margin-bottom: 0.5rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #e2e8f0;
}
.cp-consulta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.35rem 1rem;
}
.cp-consulta-row__label {
  display: block;
  font-size: 0.72rem;
  color: #64748b;
}
.cp-consulta-row__value {
  font-size: 0.88rem;
  color: #0f172a;
}
.as-search-select {
  flex: 1 1 220px;
  max-width: 420px;
}
.as-btn--buscador {
  flex: 0 0 auto;
  white-space: nowrap;
  height: 40px;
}
.as-empty--dialog {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #64748b;
}
.as-empty--dialog p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
}
.as-option {
  display: flex;
  flex-direction: column;
  padding: 2px 0;
  line-height: 1.25;
}
.as-option strong {
  font-size: 0.9rem;
  color: #0f172a;
}
.as-option span {
  font-size: 0.76rem;
  color: #64748b;
}
.as-selection-text small {
  color: #64748b;
  margin-left: 0.3rem;
}
.lg-foto {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.15rem;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.lg-foto__preview {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.lg-foto__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.lg-foto__actions {
  min-width: 0;
  flex: 1;
}
.lg-foto__hint {
  margin: 0.15rem 0 0.55rem;
  font-size: 0.78rem;
  color: #64748b;
}
.lg-foto__btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}
@media (max-width: 600px) {
  .lg-foto {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
